import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider, MutationCache } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster, toast } from 'sonner';

import { routeTree } from './routeTree.gen';
import { extractErrorMessage } from './lib/errors';
import { reloadPagesRestoredFromCache } from './lib/identity-switch';
import { useAuthStore } from './stores/auth';
import './styles/globals.css';

// Create a query client
const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    // Fallback for any mutation that doesn't already surface its own error.
    // `mutation.options.onError` only sees a HOOK-LEVEL handler (defined
    // inside `useMutation({ onError })`). A CALL-TIME override passed as
    // `someMutation.mutate(vars, { onError })` lives in the MutationObserver
    // instead and is invisible here — those mutations must be marked with
    // `meta: { suppressGlobalToast: true }` on their `useMutation({...})`
    // definition, or this fallback double-toasts alongside them.
    onError: (error, _variables, _onMutateResult, mutation) => {
      if (mutation.options.onError || mutation.meta?.suppressGlobalToast) return;
      toast.error(extractErrorMessage(error, 'Something went wrong. Please try again.'));
    },
    // Any successful mutation may change data the home-screen dashboard widgets aggregate
    // (recruitment / absences / people state). Invalidate the ['dashboard'] key centrally so
    // producer modules never have to know the dashboard exists: while the dashboard is the
    // mounted view its widgets refetch immediately; while it's inactive this only marks them
    // stale (default refetchType 'active'), and the refetch happens on next visit via the
    // setQueryDefaults below. Keeps the dashboard module fully self-contained.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

// Home-screen dashboard widgets (query keys under ['dashboard']) should reflect live app
// state on every visit / tab refocus, overriding the 5-minute default staleTime for these
// queries only. Together with the central MutationCache.onSuccess above, this keeps widgets
// fresh both when the dashboard is remounted/refocused and when a mutation fires while it's
// the active view — without any producer module referencing the dashboard.
queryClient.setQueryDefaults(['dashboard'], {
  refetchOnMount: 'always',
  refetchOnWindowFocus: 'always',
});

// Create a router instance
const router = createRouter({
  routeTree,
  context: {
    queryClient,
  },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Back after a change of identity must not restore the previous person's view. [HW-872]
reloadPagesRestoredFromCache(() => useAuthStore.getState().user?.id ?? null);

// Render the app
const rootElement = document.getElementById('root')!;
if (!rootElement.innerHTML) {
  const root = createRoot(rootElement);
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <Toaster position="top-right" richColors closeButton theme="system" />
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </StrictMode>
  );
}

